import { StatusEnum } from '@api/ringil-api.v2.tsx';
import React from 'react';
import SvgOK from '@icons/check-square.svg?react';
import SvgERROR from '@icons/alert-circle.svg?react';
import SvgWARNING from '@icons/alert.svg?react';
import SvgLoading from '@icons/loading.svg?react';

export const stateIcons: Record<StatusEnum, React.FC<React.SVGProps<SVGSVGElement>>> = {
  NEW: () => null,
  PROCESSING: SvgLoading,
  UNKNOWN: SvgWARNING,
  SKIPPED_DUE_TO_OTHER_DOCUMENT: SvgERROR,
  PUBLISH_SKIPPED_DUE_TO_DISABLED_FEATURE_FLAG: SvgWARNING,
  PROCESSED_WITH_ERRORS: SvgERROR,
  PROCESSING_FAILED: SvgERROR,
  ERP_UPLOAD_FAILED: SvgERROR,
  ERP_UPLOAD_SUCCESS: SvgOK,
  FINISHED: SvgLoading,
  USER_EDITED: SvgOK,
  ERP_FILE_UPLOAD_SUCCESS: SvgLoading,
  ERP_FILE_UPLOAD_FAILED: SvgERROR,
  MISSING_PDF_ATTACHMENT: SvgERROR,
  NO_OPS: SvgWARNING,
};

export const stateTextColors: Record<StatusEnum, string> = {
  NEW: 'text-complementary-warning',
  PROCESSING: 'text-content-secondary',
  UNKNOWN: 'text-complementary-draft',
  SKIPPED_DUE_TO_OTHER_DOCUMENT: 'text-content-tertiary',
  PUBLISH_SKIPPED_DUE_TO_DISABLED_FEATURE_FLAG: 'text-content-tertiary',
  PROCESSED_WITH_ERRORS: 'text-complementary-error',
  PROCESSING_FAILED: 'text-complementary-error',
  ERP_UPLOAD_FAILED: 'text-complementary-error',
  FINISHED: 'text-content-secondary',
  ERP_UPLOAD_SUCCESS: 'text-complementary-success',
  USER_EDITED: 'text-complementary-success',
  ERP_FILE_UPLOAD_SUCCESS: 'text-content-secondary',
  ERP_FILE_UPLOAD_FAILED: 'text-complementary-error',
  MISSING_PDF_ATTACHMENT: 'text-complementary-error',
  NO_OPS: 'text-content-draft',
};

export function getBorderClassForInvoiceState(state: StatusEnum) {
  switch (state) {
    case StatusEnum.NEW:
      return 'border-content-tertiary shadow-[0_0_4px_0_rgba(219, 211, 238, 0.4)]';
    case StatusEnum.UNKNOWN:
    case StatusEnum.SKIPPED_DUE_TO_OTHER_DOCUMENT:
    case StatusEnum.PUBLISH_SKIPPED_DUE_TO_DISABLED_FEATURE_FLAG:
      return 'border-content-tertiary shadow-[0_0_4px_0_rgba(175,175,175,0.4)]';
    case StatusEnum.PROCESSING:
    case StatusEnum.FINISHED:
    case StatusEnum.ERP_FILE_UPLOAD_SUCCESS:
      return 'border-content-tertiary shadow-[0_0_4px_0_rgba(175,175,175,0.4)]';
    case StatusEnum.PROCESSED_WITH_ERRORS:
    case StatusEnum.PROCESSING_FAILED:
    case StatusEnum.ERP_UPLOAD_FAILED:
      return 'border-complementary-error shadow-[0_0_4px_0_rgba(228,0,43,0.4)]';
    case StatusEnum.ERP_UPLOAD_SUCCESS:
      return 'border-complementary-success shadow-[0_0_4px_0_rgba(0,198,94,0.4)]';
    case StatusEnum.USER_EDITED:
      return 'border-complementary-success shadow-[0_0_4px_0_rgba(0,198,94,0.4)]';
    default:
      return 'border-content-gr2 shadow-[0_0_4px_0_rgba(0,0,0,0.1)]';
  }
}

export function getBorderAndBackgroundClassForInvoiceState(state: StatusEnum) {
  switch (state) {
    case StatusEnum.NEW:
      return getBorderClassForInvoiceState(state);
    case StatusEnum.UNKNOWN:
    case StatusEnum.SKIPPED_DUE_TO_OTHER_DOCUMENT:
    case StatusEnum.PUBLISH_SKIPPED_DUE_TO_DISABLED_FEATURE_FLAG:
      return 'bg-base-tertiary ' + getBorderClassForInvoiceState(state);
    case StatusEnum.PROCESSING:
    case StatusEnum.FINISHED:
    case StatusEnum.ERP_FILE_UPLOAD_SUCCESS:
      return getBorderClassForInvoiceState(state);
    case StatusEnum.PROCESSED_WITH_ERRORS:
    case StatusEnum.PROCESSING_FAILED:
    case StatusEnum.ERP_UPLOAD_FAILED:
      return 'bg-complementary-errorLight ' + getBorderClassForInvoiceState(state);
    case StatusEnum.ERP_UPLOAD_SUCCESS:
      return 'bg-complementary-successLight ' + getBorderClassForInvoiceState(state);
    default:
      return 'bg-content-quaternary ' + getBorderClassForInvoiceState(state);
  }
}

export interface InvoiceActionInfo {
  textKey: string;
  textDefault: string;
  emphasisKey: string;
  emphasisDefault: string;
}

const INVOICE_ACTION_INFO: Partial<Record<StatusEnum, { text: string; emphasis: string }>> = {
  [StatusEnum.ERP_FILE_UPLOAD_FAILED]: { text: 'The invoice was validated.', emphasis: 'Usually, customer action is required.' },
  [StatusEnum.ERP_UPLOAD_FAILED]: { text: 'The invoice was validated.', emphasis: 'Usually, customer action is required.' },
  [StatusEnum.MISSING_PDF_ATTACHMENT]: { text: 'The invoice PDF is missing.', emphasis: 'Usually, customer action is required.' },
  [StatusEnum.PROCESSED_WITH_ERRORS]: { text: 'This invoice contains errors.', emphasis: 'Usually, carrier action is required.' },
  [StatusEnum.PROCESSING_FAILED]: { text: 'This invoice could not be processed.', emphasis: 'Usually, carrier action is required.' },
  [StatusEnum.SKIPPED_DUE_TO_OTHER_DOCUMENT]: { text: 'This invoice is covered by another document.', emphasis: 'Usually, customer action is required.' },
};

export function getInvoiceActionInfo(status: StatusEnum): InvoiceActionInfo | null {
  const copy = INVOICE_ACTION_INFO[status];
  if (!copy) return null;
  return {
    textKey: `ai.invoice.actionInfo.${status}.text`,
    textDefault: copy.text,
    emphasisKey: `ai.invoice.actionInfo.${status}.emphasis`,
    emphasisDefault: copy.emphasis,
  };
}

export function getInvoiceMessageArgs(messageArgs?: unknown): Record<string, string> | undefined {
  return messageArgs != null && typeof messageArgs === 'object' ? (messageArgs as Record<string, string>) : undefined;
}
